import { db, storage } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  getDocs,
  arrayUnion,
  arrayRemove,
  Timestamp
} from 'firebase/firestore';
import logger from '../utils/logger';
import {
  ref,
  uploadBytesResumable,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';

/**
 * Service for managing documents in the nested structure
 * Supports 3-tier document architecture:
 * 1. Personal Documents: families/{code}/members/{userId}/personalDocuments/
 * 2. Vault (shared): families/{code}/members/{userId}/vault/
 * 3. Shared Documents: families/{code}/sharedDocuments/
 */
class DocumentService {
  /**
   * Create a personal document
   * Stored in families/{familyCode}/members/{userId}/personalDocuments/{docId}
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID who owns the document
   * @param {Object} documentData - Document data
   * @returns {Promise<string>} Document ID
   */
  async createPersonalDocument(familyCode, userId, documentData, collectionName = 'personalDocuments') {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const personalDocsCollection = collection(memberRef, collectionName);

    const docId = documentData.id || doc(personalDocsCollection).id;
    const docRef = doc(personalDocsCollection, docId);

    const docData = {
      id: docId,
      title: documentData.title,
      description: documentData.description || '',
      category: documentData.category || 'general',
      tags: documentData.tags || [],
      ownerId: userId,
      familyCode: familyCode,
      visibility: 'private', // Personal documents are always private
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: userId,
      // Conditionally include file-related fields (only for actual files, not folders)
      ...(documentData.fileUrl && { fileUrl: documentData.fileUrl }),
      ...(documentData.fileName && { fileName: documentData.fileName }),
      ...(documentData.fileType && { fileType: documentData.fileType }),
      ...(documentData.fileSize !== undefined && { fileSize: documentData.fileSize }),
      ...(documentData.storagePath && { storagePath: documentData.storagePath }),
      ...(documentData.folderPath !== undefined && { folderPath: documentData.folderPath }),
      // Conditionally include folder-specific fields
      ...(documentData.type && { type: documentData.type }),
      ...(documentData.name && { name: documentData.name })
    };

    await setDoc(docRef, docData);
    return docId;
  }

  /**
   * Get all personal documents for a user (excludes trashed items)
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of personal documents
   */
  async getPersonalDocuments(familyCode, userId, collectionName = 'personalDocuments') {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const personalDocsCollection = collection(memberRef, collectionName);

    const querySnapshot = await getDocs(personalDocsCollection);
    // Filter out deleted items
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(doc => !doc.isDeleted);
  }

  /**
   * Update a personal document
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<void>}
   */
  async updatePersonalDocument(familyCode, userId, docId, updates, collectionName = 'personalDocuments') {
    if (!familyCode || !userId || !docId) {
      throw new Error('Family code, user ID, and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const docRef = doc(memberRef, collectionName, docId);

    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date()
    });
  }

  /**
   * Soft delete a personal document (move to trash)
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @returns {Promise<void>}
   */
  async deletePersonalDocument(familyCode, userId, docId, collectionName = 'personalDocuments') {
    if (!familyCode || !userId || !docId) {
      throw new Error('Family code, user ID, and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const docRef = doc(memberRef, collectionName, docId);

    // Soft delete: mark as deleted instead of permanent deletion
    await updateDoc(docRef, {
      isDeleted: true,
      deletedAt: new Date(),
      deletedBy: userId,
      updatedAt: new Date()
    });
  }

  /**
   * Get trashed personal documents for a user
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of trashed personal documents
   */
  async getTrashedPersonalDocuments(familyCode, userId, collectionName = 'personalDocuments') {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const personalDocsCollection = collection(memberRef, collectionName);

    const querySnapshot = await getDocs(personalDocsCollection);
    // Return only deleted items
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(doc => doc.isDeleted === true);
  }

  /**
   * Restore a personal document from trash
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @returns {Promise<void>}
   */
  async restorePersonalDocument(familyCode, userId, docId, collectionName = 'personalDocuments') {
    if (!familyCode || !userId || !docId) {
      throw new Error('Family code, user ID, and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const docRef = doc(memberRef, collectionName, docId);

    // Remove deletion flags
    await updateDoc(docRef, {
      isDeleted: false,
      deletedAt: null,
      deletedBy: null,
      restoredAt: new Date(),
      updatedAt: new Date()
    });
  }

  /**
   * Permanently delete a personal document from trash
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @returns {Promise<void>}
   */
  async permanentlyDeletePersonalDocument(familyCode, userId, docId, collectionName = 'personalDocuments') {
    if (!familyCode || !userId || !docId) {
      throw new Error('Family code, user ID, and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const docRef = doc(memberRef, collectionName, docId);

    // Get document to check if it has a storage file
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const docData = docSnap.data();

      // If there's a file in storage, delete it
      if (docData.storagePath) {
        try {
          const storageRef = ref(storage, docData.storagePath);
          await deleteObject(storageRef);
        } catch (error) {
          logger.error('Error deleting file from storage:', error);
          // Continue with document deletion even if storage deletion fails
        }
      }
    }

    // Permanently delete the document
    await deleteDoc(docRef);
  }

  /**
   * Create a vault document (shareable with other family members)
   * Stored in families/{familyCode}/members/{userId}/vault/{docId}
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID who owns the document
   * @param {Object} documentData - Document data
   * @returns {Promise<string>} Document ID
   */
  async createVaultDocument(familyCode, userId, documentData) {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const vaultCollection = collection(memberRef, 'vault');

    const docId = documentData.id || doc(vaultCollection).id;
    const docRef = doc(vaultCollection, docId);

    const docData = {
      id: docId,
      title: documentData.title,
      description: documentData.description || '',
      category: documentData.category || 'vault',
      tags: documentData.tags || [],
      ownerId: userId,
      familyCode: familyCode,
      visibility: documentData.visibility || 'owner', // owner, shared, group
      sharedWith: documentData.sharedWith || [], // Array of user IDs
      sharedWithGroups: documentData.sharedWithGroups || [], // Array of group types
      permissions: {
        canView: documentData.sharedWith || [],
        canEdit: documentData.canEdit || [userId],
        canShare: documentData.canShare || [userId]
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: userId,
      // Conditionally include file-related fields (only for actual files, not folders)
      ...(documentData.fileUrl && { fileUrl: documentData.fileUrl }),
      ...(documentData.fileName && { fileName: documentData.fileName }),
      ...(documentData.fileType && { fileType: documentData.fileType }),
      ...(documentData.fileSize !== undefined && { fileSize: documentData.fileSize }),
      ...(documentData.storagePath && { storagePath: documentData.storagePath }),
      ...(documentData.folderPath !== undefined && { folderPath: documentData.folderPath }),
      // Conditionally include folder-specific fields
      ...(documentData.type && { type: documentData.type }),
      ...(documentData.name && { name: documentData.name })
    };

    await setDoc(docRef, docData);
    return docId;
  }

  /**
   * Get all vault documents for a user (excludes trashed items)
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of vault documents
   */
  async getVaultDocuments(familyCode, userId) {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const vaultCollection = collection(memberRef, 'vault');

    const querySnapshot = await getDocs(vaultCollection);
    // Filter out deleted items
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(doc => !doc.isDeleted);
  }

  /**
   * Get vault documents shared with a user
   * Searches all family members' vaults for documents shared with this user
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of shared vault documents
   */
  async getSharedVaultDocuments(familyCode, userId) {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const sharedDocs = [];
    const familyRef = doc(db, 'families', familyCode);
    const membersCollection = collection(familyRef, 'members');
    const membersSnapshot = await getDocs(membersCollection);

    // Search each member's vault for documents shared with this user
    for (const memberDoc of membersSnapshot.docs) {
      if (memberDoc.id === userId) continue; // Skip own vault

      const vaultCollection = collection(memberDoc.ref, 'vault');
      const vaultSnapshot = await getDocs(vaultCollection);

      vaultSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.sharedWith && data.sharedWith.includes(userId)) {
          sharedDocs.push({
            id: doc.id,
            ...data,
            sharedBy: memberDoc.id
          });
        }
      });
    }

    return sharedDocs;
  }

  /**
   * Share a vault document with other users or groups
   * @param {string} familyCode - Family code
   * @param {string} ownerId - Owner's user ID
   * @param {string} docId - Document ID
   * @param {Object} shareOptions - Sharing options
   * @param {Array<string>} shareOptions.userIds - User IDs to share with
   * @param {Array<string>} shareOptions.groupTypes - Group types to share with
   * @param {string} shareOptions.permission - Permission level (view, edit, share)
   * @returns {Promise<void>}
   */
  async shareVaultDocument(familyCode, ownerId, docId, shareOptions) {
    if (!familyCode || !ownerId || !docId) {
      throw new Error('Family code, owner ID, and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', ownerId);
    const docRef = doc(memberRef, 'vault', docId);

    const updates = {
      updatedAt: new Date()
    };

    if (shareOptions.userIds && shareOptions.userIds.length > 0) {
      updates.sharedWith = arrayUnion(...shareOptions.userIds);
      updates['permissions.canView'] = arrayUnion(...shareOptions.userIds);

      if (shareOptions.permission === 'edit') {
        updates['permissions.canEdit'] = arrayUnion(...shareOptions.userIds);
      }
      if (shareOptions.permission === 'share') {
        updates['permissions.canShare'] = arrayUnion(...shareOptions.userIds);
      }
    }

    if (shareOptions.groupTypes && shareOptions.groupTypes.length > 0) {
      updates.sharedWithGroups = arrayUnion(...shareOptions.groupTypes);
    }

    await updateDoc(docRef, updates);
  }

  /**
   * Revoke sharing of a vault document
   * @param {string} familyCode - Family code
   * @param {string} ownerId - Owner's user ID
   * @param {string} docId - Document ID
   * @param {Object} revokeOptions - Revoke options
   * @param {Array<string>} revokeOptions.userIds - User IDs to revoke access from
   * @param {Array<string>} revokeOptions.groupTypes - Group types to revoke access from
   * @returns {Promise<void>}
   */
  async revokeVaultDocumentSharing(familyCode, ownerId, docId, revokeOptions) {
    if (!familyCode || !ownerId || !docId) {
      throw new Error('Family code, owner ID, and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', ownerId);
    const docRef = doc(memberRef, 'vault', docId);

    const updates = {
      updatedAt: new Date()
    };

    if (revokeOptions.userIds && revokeOptions.userIds.length > 0) {
      updates.sharedWith = arrayRemove(...revokeOptions.userIds);
      updates['permissions.canView'] = arrayRemove(...revokeOptions.userIds);
      updates['permissions.canEdit'] = arrayRemove(...revokeOptions.userIds);
      updates['permissions.canShare'] = arrayRemove(...revokeOptions.userIds);
    }

    if (revokeOptions.groupTypes && revokeOptions.groupTypes.length > 0) {
      updates.sharedWithGroups = arrayRemove(...revokeOptions.groupTypes);
    }

    await updateDoc(docRef, updates);
  }

  /**
   * Update a vault document
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<void>}
   */
  async updateVaultDocument(familyCode, userId, docId, updates) {
    if (!familyCode || !userId || !docId) {
      throw new Error('Family code, user ID, and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const docRef = doc(memberRef, 'vault', docId);

    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date()
    });
  }

  /**
   * Soft delete a vault document (move to trash)
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @returns {Promise<void>}
   */
  async deleteVaultDocument(familyCode, userId, docId) {
    if (!familyCode || !userId || !docId) {
      throw new Error('Family code, user ID, and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const docRef = doc(memberRef, 'vault', docId);

    // Soft delete: mark as deleted instead of permanent deletion
    await updateDoc(docRef, {
      isDeleted: true,
      deletedAt: new Date(),
      deletedBy: userId,
      updatedAt: new Date()
    });
  }

  /**
   * Get trashed vault documents for a user
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of trashed vault documents
   */
  async getTrashedVaultDocuments(familyCode, userId) {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const vaultCollection = collection(memberRef, 'vault');

    const querySnapshot = await getDocs(vaultCollection);
    // Return only deleted items
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(doc => doc.isDeleted === true);
  }

  /**
   * Restore a vault document from trash
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @returns {Promise<void>}
   */
  async restoreVaultDocument(familyCode, userId, docId) {
    if (!familyCode || !userId || !docId) {
      throw new Error('Family code, user ID, and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const docRef = doc(memberRef, 'vault', docId);

    // Remove deletion flags
    await updateDoc(docRef, {
      isDeleted: false,
      deletedAt: null,
      deletedBy: null,
      restoredAt: new Date(),
      updatedAt: new Date()
    });
  }

  /**
   * Permanently delete a vault document from trash
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @returns {Promise<void>}
   */
  async permanentlyDeleteVaultDocument(familyCode, userId, docId) {
    if (!familyCode || !userId || !docId) {
      throw new Error('Family code, user ID, and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const docRef = doc(memberRef, 'vault', docId);

    // Get document to check if it has a storage file
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const docData = docSnap.data();

      // If there's a file in storage, delete it
      if (docData.storagePath) {
        try {
          const storageRef = ref(storage, docData.storagePath);
          await deleteObject(storageRef);
        } catch (error) {
          logger.error('Error deleting file from storage:', error);
          // Continue with document deletion even if storage deletion fails
        }
      }
    }

    // Permanently delete the document
    await deleteDoc(docRef);
  }

  /**
   * Create a shared family document
   * Stored in families/{familyCode}/sharedDocuments/{docId}
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID creating the document
   * @param {Object} documentData - Document data
   * @returns {Promise<string>} Document ID
   */
  async createSharedDocument(familyCode, userId, documentData) {
    if (!familyCode || !userId) {
      throw new Error('Family code and user ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const sharedDocsCollection = collection(familyRef, 'sharedDocuments');

    const docId = documentData.id || doc(sharedDocsCollection).id;
    const docRef = doc(sharedDocsCollection, docId);

    const docData = {
      id: docId,
      title: documentData.title,
      description: documentData.description || '',
      fileUrl: documentData.fileUrl,
      fileName: documentData.fileName,
      fileType: documentData.fileType,
      fileSize: documentData.fileSize,
      category: documentData.category || 'shared',
      tags: documentData.tags || [],
      familyCode: familyCode,
      visibility: 'family', // Visible to entire family
      visibleToGroups: documentData.visibleToGroups || ['IMMEDIATE_FAMILY'], // Which groups can see this
      uploadedBy: userId,
      permissions: {
        canView: documentData.visibleToGroups || ['IMMEDIATE_FAMILY'],
        canEdit: documentData.canEdit || ['ADMIN'],
        canDelete: documentData.canDelete || ['ADMIN']
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: userId
    };

    await setDoc(docRef, docData);
    return docId;
  }

  /**
   * Get all shared family documents (excludes trashed items)
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID (for permission filtering)
   * @returns {Promise<Array>} Array of shared documents
   */
  async getSharedDocuments(familyCode, userId) {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const sharedDocsCollection = collection(familyRef, 'sharedDocuments');

    const querySnapshot = await getDocs(sharedDocsCollection);

    // Get user's groups to filter documents
    const memberRef = doc(familyRef, 'members', userId);
    const memberSnap = await getDoc(memberRef);
    const userGroups = memberSnap.exists() ? memberSnap.data().groups || [] : [];

    // Filter documents based on user's group membership and exclude deleted items
    const docs = querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(doc => {
        // Exclude deleted items
        if (doc.isDeleted) return false;
        // Apply group filtering
        if (!doc.visibleToGroups || doc.visibleToGroups.length === 0) return true;
        return doc.visibleToGroups.some(group => userGroups.includes(group));
      });

    return docs;
  }

  /**
   * Update a shared family document
   * @param {string} familyCode - Family code
   * @param {string} docId - Document ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<void>}
   */
  async updateSharedDocument(familyCode, docId, updates) {
    if (!familyCode || !docId) {
      throw new Error('Family code and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const docRef = doc(familyRef, 'sharedDocuments', docId);

    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date()
    });
  }

  /**
   * Soft delete a shared family document (move to trash)
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID performing the delete
   * @param {string} docId - Document ID
   * @returns {Promise<void>}
   */
  async deleteSharedDocument(familyCode, userId, docId) {
    if (!familyCode || !userId || !docId) {
      throw new Error('Family code, user ID, and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const docRef = doc(familyRef, 'sharedDocuments', docId);

    // Soft delete: mark as deleted instead of permanent deletion
    await updateDoc(docRef, {
      isDeleted: true,
      deletedAt: new Date(),
      deletedBy: userId,
      updatedAt: new Date()
    });
  }

  /**
   * Get trashed shared documents for a family
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID (for permission filtering)
   * @returns {Promise<Array>} Array of trashed shared documents
   */
  async getTrashedSharedDocuments(familyCode, userId) {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const sharedDocsCollection = collection(familyRef, 'sharedDocuments');

    const querySnapshot = await getDocs(sharedDocsCollection);

    // Get user's groups to filter documents
    const memberRef = doc(familyRef, 'members', userId);
    const memberSnap = await getDoc(memberRef);
    const userGroups = memberSnap.exists() ? memberSnap.data().groups || [] : [];

    // Return only deleted items that user has permission to see
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(doc => {
        // Only include deleted items
        if (!doc.isDeleted) return false;
        // Apply group filtering
        if (!doc.visibleToGroups || doc.visibleToGroups.length === 0) return true;
        return doc.visibleToGroups.some(group => userGroups.includes(group));
      });
  }

  /**
   * Restore a shared document from trash
   * @param {string} familyCode - Family code
   * @param {string} docId - Document ID
   * @returns {Promise<void>}
   */
  async restoreSharedDocument(familyCode, docId) {
    if (!familyCode || !docId) {
      throw new Error('Family code and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const docRef = doc(familyRef, 'sharedDocuments', docId);

    // Remove deletion flags
    await updateDoc(docRef, {
      isDeleted: false,
      deletedAt: null,
      deletedBy: null,
      restoredAt: new Date(),
      updatedAt: new Date()
    });
  }

  /**
   * Permanently delete a shared document from trash
   * @param {string} familyCode - Family code
   * @param {string} docId - Document ID
   * @returns {Promise<void>}
   */
  async permanentlyDeleteSharedDocument(familyCode, docId) {
    if (!familyCode || !docId) {
      throw new Error('Family code and document ID are required');
    }

    const familyRef = doc(db, 'families', familyCode);
    const docRef = doc(familyRef, 'sharedDocuments', docId);

    // Get document to check if it has a storage file
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const docData = docSnap.data();

      // If there's a file in storage, delete it
      if (docData.storagePath) {
        try {
          const storageRef = ref(storage, docData.storagePath);
          await deleteObject(storageRef);
        } catch (error) {
          logger.error('Error deleting file from storage:', error);
          // Continue with document deletion even if storage deletion fails
        }
      }
    }

    // Permanently delete the document
    await deleteDoc(docRef);
  }

  /**
   * Move a personal document to vault
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @returns {Promise<string>} New vault document ID
   */
  async movePersonalToVault(familyCode, userId, docId, collectionName = 'personalDocuments') {
    // Get personal document
    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const personalDocRef = doc(memberRef, collectionName, docId);
    const personalDocSnap = await getDoc(personalDocRef);

    if (!personalDocSnap.exists()) {
      throw new Error('Personal document not found');
    }

    const docData = personalDocSnap.data();

    // Create vault document
    const vaultDocId = await this.createVaultDocument(familyCode, userId, docData);

    // Delete personal document
    await deleteDoc(personalDocRef);

    return vaultDocId;
  }

  /**
   * Move a vault document to shared
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @returns {Promise<string>} New shared document ID
   */
  async moveVaultToShared(familyCode, userId, docId) {
    // Get vault document
    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const vaultDocRef = doc(memberRef, 'vault', docId);
    const vaultDocSnap = await getDoc(vaultDocRef);

    if (!vaultDocSnap.exists()) {
      throw new Error('Vault document not found');
    }

    const docData = vaultDocSnap.data();

    // Create shared document
    const sharedDocId = await this.createSharedDocument(familyCode, userId, docData);

    // Delete vault document
    await deleteDoc(vaultDocRef);

    return sharedDocId;
  }

  // ============================================================================
  // File Upload & Download Methods (for UI components)
  // ============================================================================

  /**
   * Upload a file to personal documents
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {File} file - File to upload
   * @param {string} folderPath - Folder path (e.g., 'folder1/subfolder')
   * @param {Function} onProgress - Progress callback (0-100)
   * @returns {Promise<object>} Document metadata
   */
  async uploadPersonalDocument(familyCode, userId, file, folderPath = '', onProgress = null, collectionName = 'personalDocuments', category = 'general') {
    try {
      // Generate storage path for nested structure (aligned with Firestore collection name)
      const storagePath = `families/${familyCode}/members/${userId}/${collectionName}/${folderPath}/${file.name}`;
      const storageRef = ref(storage, storagePath);

      // Upload with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            if (onProgress) {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              onProgress(progress);
            }
          },
          (error) => {
            logger.error('Upload error:', error);
            reject(error);
          },
          async () => {
            try {
              // Get download URL
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

              // Create Firestore metadata with correct field names
              const docData = {
                title: file.name, // Use name as title
                description: '',
                fileUrl: downloadURL,
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,
                category,
                tags: [],
                storagePath,
                folderPath: folderPath || '/'
              };

              const docId = await this.createPersonalDocument(familyCode, userId, docData, collectionName);

              resolve({ id: docId, ...docData });
            } catch (error) {
              logger.error('Error creating metadata:', error);
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      logger.error('Error in uploadPersonalDocument:', error);
      throw error;
    }
  }

  /**
   * Upload a file to vault
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {File} file - File to upload
   * @param {string} folderPath - Folder path
   * @param {Function} onProgress - Progress callback (0-100)
   * @returns {Promise<object>} Document metadata
   */
  async uploadVaultDocument(familyCode, userId, file, folderPath = '', onProgress = null, category = 'general') {
    try {
      // Generate storage path for nested structure (aligned with Firestore collection name)
      const storagePath = `families/${familyCode}/members/${userId}/vault/${folderPath}/${file.name}`;
      const storageRef = ref(storage, storagePath);

      // Upload with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            if (onProgress) {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              onProgress(progress);
            }
          },
          (error) => {
            logger.error('Upload error:', error);
            reject(error);
          },
          async () => {
            try {
              // Get download URL
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

              // Create Firestore metadata with correct field names
              const docData = {
                title: file.name, // Use name as title
                description: '',
                fileUrl: downloadURL,
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,
                category,
                tags: [],
                storagePath,
                folderPath: folderPath || '/',
                sharedWith: []
              };

              const docId = await this.createVaultDocument(familyCode, userId, docData);

              resolve({ id: docId, ...docData });
            } catch (error) {
              logger.error('Error creating metadata:', error);
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      logger.error('Error in uploadVaultDocument:', error);
      throw error;
    }
  }

  /**
   * Get download URL for a document
   * @param {string} storagePath - Storage path from document metadata
   * @returns {Promise<string>} Download URL
   */
  async getDocumentDownloadUrl(storagePath) {
    try {
      const storageRef = ref(storage, storagePath);
      return await getDownloadURL(storageRef);
    } catch (error) {
      logger.error('Error getting download URL:', error);
      throw error;
    }
  }

  /**
   * Create a folder in personal documents
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} folderName - Folder name
   * @param {string} parentPath - Parent folder path
   * @returns {Promise<string>} Folder ID
   */
  async createFolder(familyCode, userId, folderName, parentPath = '', collectionName = 'personalDocuments') {
    const folderData = {
      title: folderName,
      name: folderName,
      type: 'folder',
      folderPath: parentPath,
      createdAt: Timestamp.now(),
      createdBy: userId
    };

    return await this.createPersonalDocument(familyCode, userId, folderData, collectionName);
  }

  // ============================================================================
  // Alias Methods (for backward compatibility with component expectations)
  // ============================================================================

  /**
   * Alias for getPersonalDocuments
   */
  async listPersonalDocuments(familyCode, userId, collectionName = 'personalDocuments') {
    return await this.getPersonalDocuments(familyCode, userId, collectionName);
  }

  /**
   * Alias for getVaultDocuments
   */
  async listVaultDocuments(familyCode, userId) {
    return await this.getVaultDocuments(familyCode, userId);
  }

  /**
   * Alias for getSharedVaultDocuments
   */
  async listSharedDocuments(familyCode, userId) {
    return await this.getSharedVaultDocuments(familyCode, userId);
  }

  /**
   * Rename a personal document or folder
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} itemId - Document/folder ID
   * @param {string} newName - New name for the item
   * @param {string} itemType - Type of item ('file' or 'folder')
   * @returns {Promise<void>}
   */
  async renamePersonalItem(familyCode, userId, itemId, newName, itemType = 'file', collectionName = 'personalDocuments') {
    if (!familyCode || !userId || !itemId || !newName) {
      throw new Error('Family code, user ID, item ID, and new name are required');
    }

    // Validate name
    const trimmedName = newName.trim();
    if (!trimmedName) {
      throw new Error('Name cannot be empty');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const docRef = doc(memberRef, collectionName, itemId);

    // Update the appropriate field based on item type
    const updates = {
      updatedAt: new Date()
    };

    if (itemType === 'folder') {
      updates.name = trimmedName;
    } else {
      updates.fileName = trimmedName;
      // Also update title to match
      updates.title = trimmedName;
    }

    await updateDoc(docRef, updates);
  }

  /**
   * Rename a vault document or folder
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID (owner)
   * @param {string} itemId - Document/folder ID
   * @param {string} newName - New name for the item
   * @param {string} itemType - Type of item ('file' or 'folder')
   * @returns {Promise<void>}
   */
  async renameVaultItem(familyCode, userId, itemId, newName, itemType = 'file') {
    if (!familyCode || !userId || !itemId || !newName) {
      throw new Error('Family code, user ID, item ID, and new name are required');
    }

    // Validate name
    const trimmedName = newName.trim();
    if (!trimmedName) {
      throw new Error('Name cannot be empty');
    }

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const docRef = doc(memberRef, 'vault', itemId);

    // Update the appropriate field based on item type
    const updates = {
      updatedAt: new Date()
    };

    if (itemType === 'folder') {
      updates.name = trimmedName;
    } else {
      updates.fileName = trimmedName;
      updates.title = trimmedName;
    }

    await updateDoc(docRef, updates);
  }

  /**
   * Rename a shared document
   * @param {string} familyCode - Family code
   * @param {string} itemId - Document/folder ID
   * @param {string} newName - New name for the item
   * @param {string} itemType - Type of item ('file' or 'folder')
   * @returns {Promise<void>}
   */
  async renameSharedItem(familyCode, itemId, newName, itemType = 'file') {
    if (!familyCode || !itemId || !newName) {
      throw new Error('Family code, item ID, and new name are required');
    }

    // Validate name
    const trimmedName = newName.trim();
    if (!trimmedName) {
      throw new Error('Name cannot be empty');
    }

    const familyRef = doc(db, 'families', familyCode);
    const docRef = doc(familyRef, 'sharedDocuments', itemId);

    // Update the appropriate field based on item type
    const updates = {
      updatedAt: new Date()
    };

    if (itemType === 'folder') {
      updates.name = trimmedName;
    } else {
      updates.fileName = trimmedName;
      updates.title = trimmedName;
    }

    await updateDoc(docRef, updates);
  }

  /**
   * Move a personal document or folder to a new folder path
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} itemId - Document/folder ID to move
   * @param {string} newFolderPath - New folder path (e.g., '/' or 'folder1/subfolder')
   * @returns {Promise<void>}
   */
  async movePersonalDocument(familyCode, userId, itemId, newFolderPath, collectionName = 'personalDocuments') {
    if (!familyCode || !userId || !itemId) {
      throw new Error('Family code, user ID, and item ID are required');
    }

    // Normalize folder path (ensure it starts with / or is /)
    const normalizedPath = newFolderPath === '' ? '/' : newFolderPath;

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const docRef = doc(memberRef, collectionName, itemId);

    // Get the document to check if it's a folder
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw new Error('Document not found');
    }

    const docData = docSnap.data();

    // If moving a folder, we need to update all items inside it recursively
    if (docData.type === 'folder') {
      const oldFolderPath = docData.folderPath || '/';
      const folderName = docData.name || docData.title;
      const currentFullPath = oldFolderPath === '/' ? `/${folderName}` : `${oldFolderPath}/${folderName}`;

      // Get all documents in this folder and subfolders
      const allDocs = await this.getPersonalDocuments(familyCode, userId, collectionName);
      const docsToUpdate = allDocs.filter(doc => {
        const docPath = doc.folderPath || '/';
        return docPath === currentFullPath || docPath.startsWith(currentFullPath + '/');
      });

      // Update all nested documents
      const updatePromises = docsToUpdate.map(doc => {
        const docPath = doc.folderPath || '/';
        let newPath;

        if (docPath === currentFullPath) {
          // Direct child of moved folder
          newPath = normalizedPath === '/' ? `/${folderName}` : `${normalizedPath}/${folderName}`;
        } else {
          // Nested deeper - replace the prefix
          const relativePath = docPath.substring(currentFullPath.length);
          newPath = normalizedPath === '/' ? `/${folderName}${relativePath}` : `${normalizedPath}/${folderName}${relativePath}`;
        }

        return this.updatePersonalDocument(familyCode, userId, doc.id, { folderPath: newPath }, collectionName);
      });

      await Promise.all(updatePromises);
    }

    // Update the item itself
    await updateDoc(docRef, {
      folderPath: normalizedPath,
      updatedAt: new Date()
    });
  }

  /**
   * Move a vault document or folder to a new folder path
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID (owner)
   * @param {string} itemId - Document/folder ID to move
   * @param {string} newFolderPath - New folder path
   * @returns {Promise<void>}
   */
  async moveVaultDocument(familyCode, userId, itemId, newFolderPath) {
    if (!familyCode || !userId || !itemId) {
      throw new Error('Family code, user ID, and item ID are required');
    }

    // Normalize folder path
    const normalizedPath = newFolderPath === '' ? '/' : newFolderPath;

    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const docRef = doc(memberRef, 'vault', itemId);

    // Get the document to check if it's a folder
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      throw new Error('Document not found');
    }

    const docData = docSnap.data();

    // If moving a folder, update all items inside it recursively
    if (docData.type === 'folder') {
      const oldFolderPath = docData.folderPath || '/';
      const folderName = docData.name || docData.title;
      const currentFullPath = oldFolderPath === '/' ? `/${folderName}` : `${oldFolderPath}/${folderName}`;

      // Get all documents in this folder and subfolders
      const allDocs = await this.getVaultDocuments(familyCode, userId);
      const docsToUpdate = allDocs.filter(doc => {
        const docPath = doc.folderPath || '/';
        return docPath === currentFullPath || docPath.startsWith(currentFullPath + '/');
      });

      // Update all nested documents
      const updatePromises = docsToUpdate.map(doc => {
        const docPath = doc.folderPath || '/';
        let newPath;

        if (docPath === currentFullPath) {
          newPath = normalizedPath === '/' ? `/${folderName}` : `${normalizedPath}/${folderName}`;
        } else {
          const relativePath = docPath.substring(currentFullPath.length);
          newPath = normalizedPath === '/' ? `/${folderName}${relativePath}` : `${normalizedPath}/${folderName}${relativePath}`;
        }

        return this.updateVaultDocument(familyCode, userId, doc.id, { folderPath: newPath });
      });

      await Promise.all(updatePromises);
    }

    // Update the item itself
    await updateDoc(docRef, {
      folderPath: normalizedPath,
      updatedAt: new Date()
    });
  }

  /**
   * Move multiple personal documents/folders to a new folder path
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {Array<string>} itemIds - Array of document/folder IDs to move
   * @param {string} newFolderPath - New folder path
   * @returns {Promise<void>}
   */
  async moveMultiplePersonalDocuments(familyCode, userId, itemIds, newFolderPath, collectionName = 'personalDocuments') {
    if (!familyCode || !userId || !itemIds || itemIds.length === 0) {
      throw new Error('Family code, user ID, and item IDs are required');
    }

    // Move all items in parallel
    const movePromises = itemIds.map(itemId =>
      this.movePersonalDocument(familyCode, userId, itemId, newFolderPath, collectionName)
    );

    await Promise.all(movePromises);
  }

  /**
   * Move multiple vault documents/folders to a new folder path
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID (owner)
   * @param {Array<string>} itemIds - Array of document/folder IDs to move
   * @param {string} newFolderPath - New folder path
   * @returns {Promise<void>}
   */
  async moveMultipleVaultDocuments(familyCode, userId, itemIds, newFolderPath) {
    if (!familyCode || !userId || !itemIds || itemIds.length === 0) {
      throw new Error('Family code, user ID, and item IDs are required');
    }

    // Move all items in parallel
    const movePromises = itemIds.map(itemId =>
      this.moveVaultDocument(familyCode, userId, itemId, newFolderPath)
    );

    await Promise.all(movePromises);
  }

  // ============================================================================
  // CROSS-STORAGE MOVE OPERATIONS
  // Move documents between different storage areas (Personal ↔ Vault)
  // ============================================================================

  /**
   * Move a document from Personal Documents to Vault
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} documentId - Document ID to move
   * @param {string} targetFolderPath - Target folder path in vault (default: '/')
   * @returns {Promise<string>} - New document ID in vault
   */
  async moveDocumentToVault(familyCode, userId, documentId, targetFolderPath = '/', collectionName = 'personalDocuments') {
    if (!familyCode || !userId || !documentId) {
      throw new Error('Family code, user ID, and document ID are required');
    }

    // Get the source document
    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const sourceRef = doc(memberRef, collectionName, documentId);

    const sourceSnap = await getDoc(sourceRef);
    if (!sourceSnap.exists()) {
      throw new Error('Document not found in personal documents');
    }

    const sourceData = sourceSnap.data();

    // Don't allow moving folders (only individual files)
    if (sourceData.type === 'folder') {
      throw new Error('Moving folders between storage areas is not supported. Please move individual files.');
    }

    // Copy file in Storage if it has a downloadURL
    let newDownloadURL = sourceData.downloadURL;
    if (sourceData.downloadURL && sourceData.storagePath) {
      try {
        // Get the old file reference
        const oldStorageRef = ref(storage, sourceData.storagePath);

        // Create new storage path for vault
        const fileName = sourceData.name || sourceData.title;
        const newStoragePath = `families/${familyCode}/members/${userId}/vault${targetFolderPath === '/' ? '' : targetFolderPath}/${Date.now()}_${fileName}`;
        const newStorageRef = ref(storage, newStoragePath);

        // Download and re-upload (Firebase doesn't have direct copy)
        const response = await fetch(sourceData.downloadURL);
        const blob = await response.blob();
        await uploadBytes(newStorageRef, blob);
        newDownloadURL = await getDownloadURL(newStorageRef);

        // Delete old file from storage
        await deleteObject(oldStorageRef).catch(() => {
          // Ignore deletion errors (file might not exist)
        });
      } catch (err) {
        logger.error('Error copying file in storage:', err);
        // Continue with metadata even if file copy fails
      }
    }

    // Create new document in vault
    const vaultRef = collection(memberRef, 'vault');
    const newDocData = {
      ...sourceData,
      downloadURL: newDownloadURL,
      storagePath: newDownloadURL ? `families/${familyCode}/members/${userId}/vault${targetFolderPath === '/' ? '' : targetFolderPath}/${Date.now()}_${sourceData.name || sourceData.title}` : null,
      folderPath: targetFolderPath,
      movedFrom: 'personalDocuments',
      movedAt: new Date(),
      updatedAt: new Date()
    };

    const newDocRef = await addDoc(vaultRef, newDocData);

    // Delete the source document
    await deleteDoc(sourceRef);

    return newDocRef.id;
  }

  /**
   * Move a document from Vault to Personal Documents
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string} documentId - Document ID to move
   * @param {string} targetFolderPath - Target folder path in personal docs (default: '/')
   * @returns {Promise<string>} - New document ID in personal documents
   */
  async moveDocumentFromVault(familyCode, userId, documentId, targetFolderPath = '/') {
    if (!familyCode || !userId || !documentId) {
      throw new Error('Family code, user ID, and document ID are required');
    }

    // Get the source document
    const familyRef = doc(db, 'families', familyCode);
    const memberRef = doc(familyRef, 'members', userId);
    const sourceRef = doc(memberRef, 'vault', documentId);

    const sourceSnap = await getDoc(sourceRef);
    if (!sourceSnap.exists()) {
      throw new Error('Document not found in vault');
    }

    const sourceData = sourceSnap.data();

    // Don't allow moving folders (only individual files)
    if (sourceData.type === 'folder') {
      throw new Error('Moving folders between storage areas is not supported. Please move individual files.');
    }

    // Copy file in Storage if it has a downloadURL
    let newDownloadURL = sourceData.downloadURL;
    if (sourceData.downloadURL && sourceData.storagePath) {
      try {
        // Get the old file reference
        const oldStorageRef = ref(storage, sourceData.storagePath);

        // Create new storage path for personal documents
        const fileName = sourceData.name || sourceData.title;
        const newStoragePath = `families/${familyCode}/members/${userId}/personalDocuments${targetFolderPath === '/' ? '' : targetFolderPath}/${Date.now()}_${fileName}`;
        const newStorageRef = ref(storage, newStoragePath);

        // Download and re-upload (Firebase doesn't have direct copy)
        const response = await fetch(sourceData.downloadURL);
        const blob = await response.blob();
        await uploadBytes(newStorageRef, blob);
        newDownloadURL = await getDownloadURL(newStorageRef);

        // Delete old file from storage
        await deleteObject(oldStorageRef).catch(() => {
          // Ignore deletion errors (file might not exist)
        });
      } catch (err) {
        logger.error('Error copying file in storage:', err);
        // Continue with metadata even if file copy fails
      }
    }

    // Create new document in personal documents
    const personalDocsRef = collection(memberRef, 'personalDocuments');
    const newDocData = {
      ...sourceData,
      downloadURL: newDownloadURL,
      storagePath: newDownloadURL ? `families/${familyCode}/members/${userId}/personalDocuments${targetFolderPath === '/' ? '' : targetFolderPath}/${Date.now()}_${sourceData.name || sourceData.title}` : null,
      folderPath: targetFolderPath,
      movedFrom: 'vault',
      movedAt: new Date(),
      updatedAt: new Date(),
      // Remove vault-specific fields
      sharedWith: undefined,
      accessLevel: undefined
    };

    // Clean undefined fields
    Object.keys(newDocData).forEach(key => {
      if (newDocData[key] === undefined) {
        delete newDocData[key];
      }
    });

    const newDocRef = await addDoc(personalDocsRef, newDocData);

    // Delete the source document
    await deleteDoc(sourceRef);

    return newDocRef.id;
  }

  /**
   * Move multiple documents between storage areas
   * @param {string} familyCode - Family code
   * @param {string} userId - User ID
   * @param {string[]} documentIds - Array of document IDs to move
   * @param {string} sourceArea - Source storage area ('personal' or 'vault')
   * @param {string} targetArea - Target storage area ('personal' or 'vault')
   * @param {string} targetFolderPath - Target folder path (default: '/')
   * @returns {Promise<string[]>} - Array of new document IDs
   */
  async moveBetweenStorageAreas(familyCode, userId, documentIds, sourceArea, targetArea, targetFolderPath = '/') {
    if (!familyCode || !userId || !documentIds?.length) {
      throw new Error('Family code, user ID, and document IDs are required');
    }

    if (sourceArea === targetArea) {
      throw new Error('Source and target storage areas cannot be the same');
    }

    const movePromises = documentIds.map(async (docId) => {
      if (sourceArea === 'personal' && targetArea === 'vault') {
        return this.moveDocumentToVault(familyCode, userId, docId, targetFolderPath);
      } else if (sourceArea === 'vault' && targetArea === 'personal') {
        return this.moveDocumentFromVault(familyCode, userId, docId, targetFolderPath);
      } else {
        throw new Error(`Invalid storage area combination: ${sourceArea} -> ${targetArea}`);
      }
    });

    return Promise.all(movePromises);
  }
}

// Create and export a singleton instance
export const documentService = new DocumentService();
